import { useStrings } from "@1js/localization-components";
import {
  Button,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Spinner,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import { Dismiss32Regular } from "@fluentui/react-icons";
import * as React from "react";
import { useEffect, useRef } from "react";
import {
  ConfirmationDialogBody,
  ConfirmationDialogClose,
  ConfirmationDialogDeleting,
  ConfirmationDialogStopSharing,
  ConfirmationDialogTitle,
} from "./ManageSharedPrompts.strings";

type ConfirmationDialogProps = {
  deletePrompt: () => void;
  cancelDelete: () => void;
  isLoading?: boolean;
};

export const ConfirmationDialog = ({
  deletePrompt,
  cancelDelete,
  isLoading,
}: ConfirmationDialogProps) => {
  const strings = useStrings({
    ConfirmationDialogBody,
    ConfirmationDialogTitle,
    ConfirmationDialogClose,
    ConfirmationDialogStopSharing,
    ConfirmationDialogDeleting,
  });

  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Focus on the primary button when the dialog appears
    if (!isLoading && primaryButtonRef.current) {
      primaryButtonRef.current.focus();
    }
  }, [isLoading]);

  return (
    <DialogBody>
      <DialogTitle
        id="dialog-title-id"
        action={
          <Tooltip
            content={strings.ConfirmationDialogClose()}
            relationship="label"
          >
            <DialogTrigger action="close">
              <Button
                appearance="subtle"
                aria-label={strings.ConfirmationDialogClose()}
                icon={<Dismiss32Regular />}
              />
            </DialogTrigger>
          </Tooltip>
        }
      >
        {strings.ConfirmationDialogTitle()}
      </DialogTitle>
      <DialogContent>
        <Text>{strings.ConfirmationDialogBody()}</Text>
      </DialogContent>
      <DialogActions>
        {isLoading ? (
          <Spinner
            size="tiny"
            labelPosition="after"
            label={strings.ConfirmationDialogDeleting()}
          />
        ) : (
          <Button
            appearance="primary"
            onClick={deletePrompt}
            tabIndex={0}
            ref={primaryButtonRef}
          >
            {strings.ConfirmationDialogStopSharing()}
          </Button>
        )}
        <Button
          appearance="secondary"
          disabled={isLoading}
          onClick={cancelDelete}
        >
          {strings.ConfirmationDialogClose()}
        </Button>
      </DialogActions>
    </DialogBody>
  );
};
